<script lang="ts">
export default {
  name: 'Renewals',
};
</script>

<script setup lang="ts">
import { ProgramRateGroup } from '@/types/ProgramRates';
import { filters } from '@/utilities/Filters';
import { exportToPDF, exportToPNG, getChartImages, getHeaderImage, getPNG } from '@/utilities/ExportCharts.js';
import { ref } from 'vue';
import { ExternalLink } from '@lucide/vue';
import ExportDropdown from '@/components/ExportDropdown.vue';
import { DownloadTableToCSV } from '@/utilities/Download.js';

const exportFormats = ['png', 'pdf', 'csv'];

const isExporting = ref(false);

const props = defineProps<{
  accountId: string | number;
  accountName?: string;
  programRateGroups: ProgramRateGroup[];
}>();

function prefixValue(val: number): string {
  return val > 0 ? '+' : '';
}

async function handleExport(type: string, index: number, group: ProgramRateGroup, callback: () => void) {
  isExporting.value = true;
  const groupName = group.coverageType + (group.exposureName ? ` - ${group.exposureName}` : '');

  // gather element IDs for each chart
  const elementIds = [`renewal-${index}`];

  // create the PNG image that will be used for exporting
  const images = await getChartImages(elementIds);

  const maxWidth = Math.max(...images.map(({ width }) => width));
  const header = await getHeaderImage(maxWidth, `${props.accountName ?? ''} - ${groupName}`);

  // create the PNG image that will be used for exporting
  const png = await getPNG(images, header);

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split('T')[0].replace(/-/g, '.');
  const filename = `${props.accountName ?? ''}_${groupName}_${formattedDate}`;

  if (type === 'png') {
    await exportToPNG(png, filename + '.png');
  } else if (type === 'pdf') {
    await exportToPDF(images, header, filename + '.pdf');
  } else if (type === 'csv') {
    DownloadTableToCSV(`#renewal-table-${index}`, filename);
  } else {
    console.log(`unexpected value ${type} for export type`);
  }

  isExporting.value = false;
  callback();
}
</script>

<template>
  <div>
    <div v-for="(group, index) in props.programRateGroups" :id="`renewal-${index}`" :key="index" class="py-8">
      <div class="mb-1 flex items-center justify-items-center gap-4">
        <h3>{{ group.coverageType }}{{ group.exposureName ? ` - ${group.exposureName}` : '' }}</h3>
        <ExportDropdown
          class="export-hide"
          :exportType="exportFormats"
          @export="(type, callback) => handleExport(type, index, group, callback)" />
      </div>
      <table v-tablesort class="apptable dense programs" :id="`renewal-table-${index}`">
        <thead>
          <tr>
            <td style="width: 14%">Program</td>
            <td style="width: 14%">Period</td>
            <td style="width: 12%">Retention</td>
            <td style="width: 15%">Occurrence Limit</td>
            <td style="width: 15%">Aggregate Limit</td>
            <td style="width: 12%">Premium</td>

            <td style="width: 11%" v-if="group.showExposure">{{ group.exposureType }}</td>

            <td style="width: 17%" v-if="group.showExposure">Rate per<br />{{ group.exposureType }}</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="period in group.periods" :key="`${group.coverageType}-${period.year}`">
            <td>
              <router-link :to="`/account/${accountId}/programs/${period.programID}`">
                <div class="flex items-center justify-between gap-1">
                  {{ period.programName }}
                  <ExternalLink :size="16" class="text-primary" />
                </div>
              </router-link>
            </td>
            <td>{{ period.year }}</td>
            <td>
              <div class="program-detail">
                {{ filters.money(period.coverageRetention, 0, 0) ?? '--' }}
                <span v-if="!!period.retentionChange" class="change">
                  {{ prefixValue(period.retentionChange)
                  }}{{ filters.compactNumber(period.retentionChange * 100, true) }}%
                </span>
              </div>
            </td>
            <td>
              <div class="program-detail">
                {{ period.coverageOccLimit ? filters.money(period.coverageOccLimit, 0, 0) : '--' }}
                <span v-if="!!period.occLimitChange && period.coverageOccLimit" class="change">
                  {{ prefixValue(period.occLimitChange)
                  }}{{ filters.compactNumber(period.occLimitChange * 100, true) }}%
                </span>
              </div>
            </td>
            <td>
              <div class="program-detail">
                {{ period.coverageAggLimit ? filters.money(period.coverageAggLimit, 0, 0) : '--' }}
                <span v-if="!!period.aggLimitChange && period.coverageAggLimit" class="change">
                  {{ prefixValue(period.aggLimitChange)
                  }}{{ filters.compactNumber(period.aggLimitChange * 100, true) }}%
                </span>
              </div>
            </td>
            <td>
              <div class="program-detail">
                {{ filters.compactMoney(period.coveragePremium) ?? '--' }}
                <span v-if="!!period.premiumChange" class="change">
                  {{ prefixValue(period.premiumChange) }}{{ filters.compactNumber(period.premiumChange * 100, true) }}%
                </span>
              </div>
            </td>
            <td v-if="group.showExposure">
              <div class="program-detail">
                {{ filters.compactNumber(period.exposureValue) ?? '--' }}
                <span v-if="!!period.exposureChange" class="change">
                  {{ prefixValue(period.exposureChange)
                  }}{{ filters.compactNumber(period.exposureChange * 100, true) }}%
                </span>
              </div>
            </td>
            <td v-else class="invisible border-0">&nbsp;</td>
            <td v-if="group.showExposure">
              <div class="program-detail">
                {{ period.rate ?? '--' }}
                <span v-if="period.rateChange" class="change">
                  {{ prefixValue(period.rateChange) }}{{ filters.compactNumber(period.rateChange * 100, true) }}%
                </span>
              </div>
            </td>
            <td v-else class="invisible border-0">&nbsp;</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
table {
  table-layout: fixed;
  width: 100%;
}

table td {
  word-break: normal;
  white-space: normal !important;

  border-left: 1px solid #e6e6e6;
}

table thead tr td {
  vertical-align: bottom;
}

table td:first-child {
  border-left: none;
}

table > thead > tr > td {
  border-left: none;
}

div.program-detail {
  display: flex;
  gap: 0.5rem;
}

.change {
  font-style: italic;
}
</style>
